import React from 'react';
import { ShowMore } from '@/components/index';
import { useAppConfigContext } from '@/context/AppConfig';
import { SocialProfileSvg } from '@/reports/contact/components';
import {
  emailsByProfessionalPriority,
  bestPhone,
} from '@/reports/contact/utils';
import { ReportSectionStyled } from '@/reports/shared/ReportSection/ReportSection';
import { peopleTypes, commonTypes } from '@/reports/types';
import { ContactCompany } from '@/search/index';
import {
  Stack,
  Icons,
  Text,
  Pill,
  SwitchLabel,
  useTheme,
  Link,
  Box,
} from '@/theme/index';
import { filterByMainWebsites } from '@/utils/data';
import {
  titleize,
  nameize,
  fetchJobTitle,
  formatCompany,
  formatLocation,
  formatPhone,
} from '@/utils/index';

const formatJobDetails = (job: peopleTypes.Job) => {
  if (job?.title || job?.company) {
    return `${fetchJobTitle(job)} at ${formatCompany(job?.company ?? '-')}`;
  }
  return 'No Jobs found';
};

const formatLocationDetails = (address: commonTypes.Address | undefined) => {
  if (address?.parsed?.city || address?.parsed?.state) {
    return formatLocation(
      address?.parsed?.city ?? '',
      address?.parsed?.state ?? ''
    );
  }
  return null;
};

function getUniqueProfiles(profiles: peopleTypes.Profiles) {
  const uniqueProfiles: Record<string, peopleTypes.Profiles[number]> = {};

  profiles.forEach((profile) => {
    const site = profile.site || '';
    if (!uniqueProfiles[site]) {
      uniqueProfiles[site] = profile;
    }
  });

  return Object.values(uniqueProfiles);
}

interface ContactInfoRowProps {
  /** The icon to display on the left side of the row. */
  icon: React.ReactNode;
  /** The label to display on the left side of the row. */
  label: string;
  /** The value to display on the right side of the row. */
  value: string | JSX.Element;
  /** The data point to be used for tracking. */
  dataPoint: string;
}

/**
 * Representing a information row in the contact overview section.
 */
const ContactInfoRow: React.FC<ContactInfoRowProps> = ({
  icon,
  label,
  value,
  dataPoint,
}) => {
  const theme = useTheme();
  const { trackEvent } = useAppConfigContext();

  const handleTrackEvent = () => {
    trackEvent(
      `overview_current ${dataPoint}`,
      'engagement click',
      'contact report'
    );
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="start"
      sx={{
        [theme.breakpoints.up('sm')]: {
          alignItems: 'center',
        },
      }}
    >
      {icon}
      <Text fontWeight={theme.typography.fontWeightBold} width={110}>
        {label}
      </Text>
      <Text
        width={160}
        onClick={handleTrackEvent}
        sx={{
          cursor: 'pointer',
          wordBreak: 'break-word',
          [theme.breakpoints.up('sm')]: {
            width: 'fit-content',
            wordBreak: 'normal',
          },
        }}
      >
        {value}
      </Text>
    </Stack>
  );
};

interface SocialProfileRowProps {
  /** The list of social profiles to display. */
  profiles: peopleTypes.Profiles;
}

/**
 * Representing a social media row in the contact overview section
 */
const SocialProfileRow: React.FC<SocialProfileRowProps> = ({ profiles }) => {
  const theme = useTheme();
  const { trackEvent } = useAppConfigContext();

  const handleTrackEvent = (dataPoint: string) => {
    trackEvent(`overview_${dataPoint}`, 'engagement click', 'contact report');
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Icons.Share fontSize="small" />
      <Text fontWeight={theme.typography.fontWeightBold} width={110}>
        Social media
      </Text>
      {profiles.length > 0 ? (
        <Stack
          direction="row"
          alignItems="center"
          width={160}
          flexWrap="wrap"
          sx={{
            [theme.breakpoints.up('sm')]: {
              width: 'fit-content',
              flexWrap: 'nowrap',
            },
          }}
        >
          {profiles.map((profile) => (
            <Box
              key={profile.domain}
              onClick={() => handleTrackEvent(profile.site || '')}
              marginTop={1}
              marginRight={2}
              sx={{ cursor: 'pointer' }}
            >
              <SocialProfileSvg
                key={profile.domain}
                site={profile.domain || ''}
              />
            </Box>
          ))}
        </Stack>
      ) : (
        <Text>No social media profiles found</Text>
      )}
    </Stack>
  );
};

export interface ContactOverviewSectionProps {
  /** The name of the person. */
  personName: string;
  /** If true, the **High Confidence Data Only** switch will be displayed. */
  showHighConfidenceToggle?: boolean;
  /** If true, only high confidence data will be displayed. */
  showHighConfidenceDataOnly: boolean;
  /** Callback fired when the user toggles the **High Confidence Data Only** switch. */
  setShowHighConfidenceDataOnly: (value: boolean) => void;
  /** The function to calculate the url to be used in the company name. Mainly for accessibility purposes */
  getCompanyUrl?: (companyName: string) => string;
  /** The handler for the click event in the company names associated to the contact */
  onCompanyClick: (company: string | ContactCompany) => void;
  /** The data to be passed in to the overview items */
  contactOverviewItemsData: {
    job: peopleTypes.Job;
    address: commonTypes.Address;
    emails: commonTypes.Emails;
    phones: commonTypes.Phones;
    profiles: peopleTypes.Profiles;
    education: peopleTypes.Education;
    identity?: string[];
  };
}

/**
 * Represents the contact overview section in the contact report.
 */
export function ContactOverviewSection({
  personName,
  showHighConfidenceToggle,
  showHighConfidenceDataOnly,
  setShowHighConfidenceDataOnly,
  getCompanyUrl,
  onCompanyClick,
  contactOverviewItemsData: {
    job,
    address,
    emails,
    phones,
    profiles,
    education,
    identity,
  },
}: ContactOverviewSectionProps) {
  const theme = useTheme();
  const filteredProfiles = filterByMainWebsites(
    profiles.filter((profile) => profile.domain)
  );
  const locationDetails = formatLocationDetails(address);

  const handleCompanyClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    company: string | ContactCompany
  ) => {
    event.preventDefault();
    onCompanyClick(company);
  };

  const companyHref = (companyName: string) => {
    if (getCompanyUrl) {
      return getCompanyUrl(companyName);
    }

    return undefined;
  };

  return (
    <ReportSectionStyled id="contact-overview">
      <Stack spacing={1} direction="row" alignItems="center">
        <Icons.AccountCircle color="primary" sx={{ fontSize: '4.5rem' }} />
        <Stack spacing={1}>
          <Stack
            sx={{
              [theme.breakpoints.up('sm')]: {
                flexDirection: 'row',
                alignItems: 'end',
              },
            }}
          >
            <Text fontSize={34} fontWeight={theme.typography.fontWeightBold}>
              {nameize(personName || '')}
            </Text>
            {locationDetails && (
              <Pill
                icon={<Icons.LocationOn sx={{ fontSize: '1rem' }} />}
                label={locationDetails}
                color="secondary"
                className="badge"
                sx={{
                  width: 'fit-content',
                  color: theme.palette.primary.main,
                  [theme.breakpoints.up('sm')]: {
                    marginLeft: theme.spacing(1.5),
                  },
                }}
              />
            )}
          </Stack>
          <Link
            href={companyHref(job?.company ?? '')}
            underline="hover"
            fontWeight={theme.typography.fontWeightBold}
            onClick={(event) => handleCompanyClick(event, job?.company ?? '')}
          >
            {formatJobDetails(job)}
          </Link>
        </Stack>
      </Stack>
      <Stack spacing={2} marginTop={theme.spacing(3)}>
        <ContactInfoRow
          icon={<Icons.Email fontSize="small" />}
          label="Current email"
          value={
            emailsByProfessionalPriority(emails)[0]?.address ||
            'No email addresses found'
          }
          dataPoint="email"
        />
        <ContactInfoRow
          icon={<Icons.PhoneIphone fontSize="small" />}
          label="Current phone"
          value={formatPhone(
            bestPhone(phones)?.number || 'No phone numbers found'
          )}
          dataPoint="phone"
        />
        <SocialProfileRow profiles={getUniqueProfiles(filteredProfiles)} />
        <ContactInfoRow
          icon={<Icons.School fontSize="small" />}
          label="Education"
          value={
            titleize(education?.school || '', { lowercase: true }) ||
            'No education found'
          }
          dataPoint="education"
        />
        <ContactInfoRow
          icon={<Icons.Person fontSize="small" />}
          label="Aliases"
          value={
            <ShowMore
              items={identity ?? []}
              initialItems={2}
              expandedLabel="Hide"
              collapsedLabel={`View ${identity?.length ?? 0 - 2} more`}
              hideExtraItems
            />
          }
          dataPoint="Aliases"
        />
      </Stack>
      {showHighConfidenceToggle && (
        <SwitchLabel
          label="High Confidence Data Only"
          tooltip="High confidence data is the data our algorithms predict could be the most reliable. Since we aggregate data from public records, certain pieces of information may be corroborated by multiple sources, increasing our potential confidence in the data. You can turn this feature on or off at any time - the report will automatically update."
          checked={showHighConfidenceDataOnly}
          setChecked={setShowHighConfidenceDataOnly}
          stackStyles={{ justifyContent: 'unset', marginTop: 1 }}
          tooltipStart
          reverseOrder
        />
      )}
    </ReportSectionStyled>
  );
}

ContactOverviewSection.defaultProps = {
  showHighConfidenceToggle: true,
  getCompanyUrl: undefined,
};
